import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { api, errorMessage } from '../api/client';
import { ConnectClaudeHint } from '../components/ConnectClaudeHint';
import { AddMachineDialog } from './machines/AddMachineDialog';
import { CloudPodsInstalling } from './machines/CloudPodsInstalling';
import { MACHINES_KEY } from './machines/MachineActions';
import { MachineCard } from './machines/MachineCard';
import { NewCloudMachineDialog } from './machines/NewCloudMachineDialog';

export function MachinesPage() {
  const queryClient = useQueryClient();
  const machines = useQuery({ queryKey: MACHINES_KEY, queryFn: api.listMachines, refetchInterval: 10_000, refetchOnWindowFocus: true });
  const [adding, setAdding] = useState<'home' | 'cloud' | null>(null);
  const [added, setAdded] = useState(false);
  const close = useCallback(() => { setAdding(null); }, []);
  const paired = useCallback(() => {
    setAdding(null);
    setAdded(true);
    void queryClient.invalidateQueries({ queryKey: MACHINES_KEY });
  }, [queryClient]);
  const list = machines.data?.machines ?? [];
  const open = (kind: 'home' | 'cloud') => { setAdded(false); setAdding(kind); };
  const addButton = (
    <div className="add-buttons">
      <button type="button" onClick={() => { open('home'); }}>Add machine</button>
      <button type="button" className="button-quiet" onClick={() => { open('cloud'); }}>New cloud machine</button>
    </div>
  );
  return (
    <section aria-labelledby="machines-title">
      <div className="page-head">
        <h1 id="machines-title">My machines</h1>
        {list.length > 0 && addButton}
      </div>
      {added && <p role="status" className="success">Machine added.</p>}
      {machines.isPending && <p role="status">Loading machines…</p>}
      {machines.isError && <p role="alert" className="form-error">{errorMessage(machines.error)}</p>}
      {machines.isSuccess && list.length === 0 && (
        <div className="empty">
          <p className="empty-title">No machines yet.</p>
          <p>Your Mac or PC does the computing; Couch shows its ComfyUI here. Add one with a single pasted line.</p>
          {addButton}
        </div>
      )}
      <CloudPodsInstalling />
      <div className="machine-grid">{list.map((m) => <MachineCard key={m.id} machine={m} latest={machines.data?.latest} />)}</div>
      {list.length > 0 && <ConnectClaudeHint />}
      {adding === 'home' && <AddMachineDialog onClose={close} onPaired={paired} />}
      {adding === 'cloud' && <NewCloudMachineDialog onClose={close} onPaired={paired} />}
    </section>
  );
}
